/**
 * feat-317 — the heading above one per-role scoring grid (design/NOTES.md §1).
 *
 * Rendered ONLY when two or more role grids are visible (D6). With one grid the
 * surface renders exactly today's markup: no heading, no wrapper.
 *
 * Text is `group.display_name` verbatim — no "Role:" prefix, no shift count. It
 * wraps and never truncates: it is the only thing telling two grids apart. It is
 * deliberately NOT a pill, so it never reads as an RBAC role badge.
 */
import React from 'react';

export const ROLE_GRID_HEADING_CLASS =
  'font-display text-lg font-semibold text-ink leading-snug mb-3 break-words';

interface RoleGridHeadingProps {
  /** One level below the surrounding heading (NOTES §1): h3 under an h2, h2 under an h1. */
  as?: 'h2' | 'h3';
  displayName: string | null;
  /** Falls back to the canonical role key when display_name is null. */
  role?: string | null;
}

export const roleGridLabel = (displayName: string | null, role?: string | null): string =>
  displayName ?? role ?? '';

const RoleGridHeading: React.FC<RoleGridHeadingProps> = ({ as = 'h3', displayName, role }) => {
  const Tag = as;
  return (
    <Tag className={ROLE_GRID_HEADING_CLASS} data-testid="role-grid-heading">
      {roleGridLabel(displayName, role)}
    </Tag>
  );
};

/**
 * In-card "role band" variant (NOTES §1, §4.4) for Manager/Store tables whose
 * grids share one card. The first band drops its top border because the card
 * header already draws that line.
 */
export const RoleBand: React.FC<{ displayName: string | null; role?: string | null; first: boolean }> = ({
  displayName,
  role,
  first,
}) => (
  <div className={`px-6 pt-5 pb-3${first ? '' : ' border-t border-softBorder'}`}>
    <h3
      className="font-display text-lg font-semibold text-ink leading-snug break-words"
      data-testid="role-grid-heading"
    >
      {roleGridLabel(displayName, role)}
    </h3>
  </div>
);

export default RoleGridHeading;
